<script setup>
/* eslint-disable no-unused-vars */
/* global defineProps, defineEmits */
import clockifyService from "@/api/services/clockify.service";
import ticketService from "@/api/services/ticket.service";
import notificationService from "@/utils/plugins/NotificationService";
import SharedDialog from "@/components/shared/SharedDialog.vue";

import { computed, getCurrentInstance, onMounted, ref } from "vue";
import { useAuthStore } from "@/app/stores/authStore";
import { useUiStore } from "@/app/stores/uiStore";


const props = defineProps({
  link: {
    type: String,
    required: true
  },
  clockifyProjectId: {
    type: String,
    required: true
  },
  clockifyTagId: {
    type: String,
    required: true
  },
  ticket: {
    type: Object,
    required: true
  },
  // Código del estado actual del ticket (el ticket recibido solo trae el grupo ABIERTO/EN CURSO/CERRADO)
  statusCode: {
    type: String,
    default: null
  },
  // Si el flujo permite pasar el ticket a Trabajando desde su estado, y cómo hacerlo (devuelve true si lo logra)
  canWork: {
    type: Boolean,
    default: false
  },
  ensureWorking: {
    type: Function,
    default: null
  },
  // Asignado actual del formulario; el ticket recibido puede estar desfasado
  assignedEmployee: {
    type: [String, Object],
    default: null
  }
});

const emit = defineEmits(["updateTicket"]);

const authStore = useAuthStore();
const uiStore = useUiStore();
const user = computed(() => authStore.getUserLogged);

const instance = getCurrentInstance();

const timeEntryId = ref(null);

const showStartDialog = ref(false);

const currentEntry = ref(null);

const referenceOf = value => (value && typeof value === 'object' ? value['@id'] : value) || null;

const isAssignedUser = computed(() => {
  const assigned = referenceOf(props.assignedEmployee ?? props.ticket.assignedEmployee);
  return !!assigned && user.value?.['@id'] === assigned;
});

const changedType = computed(() => {
  return currentEntry.value && !currentEntry.value.tagIds.includes(props.ticket.ticketType.clockifyTagId);
});

const disabledStart = computed(() => {
  return (props.statusCode !== 'working' && !props.canWork) || !isAssignedUser.value;
});

function toggleShowDialog() {
  showStartDialog.value = !showStartDialog.value;
}

async function start() {
  if (disabledStart.value) {
    notificationService.addNotification({
      type: 'error',
      message: 'Debes tener el ticket asignado y en un estado desde el que se pueda pasar a Trabajando para poder crear la entrada de tiempo.',
      duration: 10000,
      showClose: true
    });
    return;
  }
  // Si el ticket no está en Trabajando (Abierto, En espera o Reabierto), se pasa a Trabajando antes de arrancar el reloj
  if (props.statusCode !== 'working' && !(await props.ensureWorking?.())) {
    return;
  }
  await clockifyService.startTime(user.value.clockify['api-key'], {
    "billable": true,
    "customFields": [],
    "description": '[' + props.ticket.tag + '] - ' + props.ticket.id + ' - ' + props.ticket.issue,
    "projectId": props.clockifyProjectId,
    //"5dca96fd9fbfdb5c52836240"
    "start": new Date().toISOString(),
    "tagIds": [props.clockifyTagId, '6582afa73119622b93ba2eb8'] // Add the tag id of the ticket type 'Tickets'
  }).then(response => {
    if (response && typeof response !== "undefined" && response.status && (response.status === 201 || response.status === 200)) {
      timeEntryId.value = response.data.id;
      currentEntry.value = response.data;
      ticketService.updateTicket({
        timeEntryId: timeEntryId.value
      }, props.ticket.id);
      emit('updateTicket');
    }
  });
}

async function updateEntry() {
  currentEntry.value.tagIds = [props.clockifyTagId, '6582afa73119622b93ba2eb8'];
  await clockifyService.updateEntry(user.value.clockify['api-key'], currentEntry.value.id, {
    tagIds: currentEntry.value.tagIds
  });
  await stop();
}

async function stop(id = null) {
  uiStore.startLoading();
  try {
    const actualId = id ? id : timeEntryId.value;
    const response = await clockifyService.getTimeEntry(user.value.clockify['api-key'], actualId);
    const entry = response.data;
    entry.end = new Date().toISOString();
    entry.start = response.data.timeInterval.start;
    entry.projectId = response.data.projectId || null;
    await clockifyService.stopTime(user.value.clockify['api-key'], actualId, entry);
    if (!id) {
      timeEntryId.value = null;
      await ticketService.updateTicket({
        timeEntryId: null
      }, props.ticket.id);
    }
  } catch (error) {
    notificationService.error(error);
  } finally {
    showStartDialog.value = false;
    uiStore.stopLoading();
  }
}

onMounted(async () => {
  try {
    if (props.ticket.timeEntryId) {
      await clockifyService.getTimeEntry(user.value.clockify['api-key'], props.ticket.timeEntryId).then(response => {
        let entry = response.data;
        if (!entry.timeInterval.end) {
          timeEntryId.value = entry.id;
          currentEntry.value = entry;
        } else {
          timeEntryId.value = null;
          instance.proxy.icon = '@/assets/icons/stop-red.svg';
          instance.proxy.alt = 'Parar reloj';
        }
      }).catch(() => {
        timeEntryId.value = null;
        ticketService.updateTicket({
          timeEntryId: null
        }, props.ticket.id);
      });
    } else {
      await clockifyService.getTimeEntries(user.value.clockify['api-key'], user.value.clockify.id).then(response => {
        let entry = null;
        let entries = response.data;
        if (entries.length > 0) {
          entry = entries[0];
          // La entrada se crea con la descripción '[TAG] - id - asunto'
          if (entry.description === '[' + props.ticket.tag + '] - ' + props.ticket.id + ' - ' + props.ticket.issue) {
            timeEntryId.value = entry.id;
            currentEntry.value = entry;
            instance.proxy.icon = '@/assets/icons/stop-red.svg';
            instance.proxy.alt = 'Parar reloj';
            ticketService.updateTicket({
              timeEntryId: timeEntryId.value
            }, props.ticket.id);
          } else {
            stop(entries[0].id);
          }
        }
      }).catch(error => {
        console.log(error);
        notificationService.error(error);
      });
    }
  } catch (error) {
    console.log(error);
    // notificationService.error(error)
  }
});
</script>

<template>
  <div class="clocky-container">
    <a target="_blank" @click="!timeEntryId ? start() : (changedType ? toggleShowDialog() : stop())">
      Clockify
      <img src="@/assets/icons/clockify-blue.svg" alt="clockify">
      <img v-if="!timeEntryId" src="@/assets/icons/play-blue.svg" alt="Arrancar reloj">
      <img v-else src="@/assets/icons/stop-red.svg" alt="Parar reloj">
    </a>
    <SharedDialog v-if="showStartDialog" title="La entrada de tiempo en curso tiene una etiqueta diferente al actual
           que se le ha asignado al ticket según su tipo" :close-icon="true"
      confirm-text="Cambiar etiqueta y parar reloj" decline-text="Solo parar reloj"
      message="¿Deseas cambiar la etiqueta de la entrada de tiempo en curso a la del tipo actual del ticket?"
      @confirm="updateEntry()" @close="toggleShowDialog()" @decline="stop()" />
  </div>
</template>

<style scoped lang="scss">
.clocky-container {
  cursor: default;
  border-radius: 16px;
  background-color: white;
  width: 360px;
  height: 85px;
  color: #474CE6;
  font-family: Montserrat;
  font-size: 23px;
  font-weight: 800;
  line-height: 28px;
  letter-spacing: -0.02em;
  text-align: left;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
}

img {
  &:first-child {
    margin-left: 5px;
  }

  margin-right: 3px;
  width: 58px;
  height: 64px;
}
</style>
